import * as React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
} from '@mui/material';

const sections = [
  {
    id: 'menu',
    title: '1. Керування головним меню.',
    html: `Для керування головним меню використовується цей <a href="/content">розділ</a>.<br>
    Бот працює за таким принципом: користувач натискає кнопки та отримує нові повідомлення. Будь-яке повідомлення від бота — це <b>блок</b>.<br>
    Структура головного меню виглядає так:<br>
    <img class="instruction-image" src="/instruction/1.png"/><br>
    Пропоную переглянути налаштування головного меню:<br>
    <img class="instruction-image" src="/instruction/2.png"/><br>

    <b>Назва</b>: "Головне меню". Використовується лише для зручності в адмінці та не впливає на роботу бота.<br>
    
    <b>Тип клавіатури</b>: Telegram підтримує два типи клавіатури:<br>
    <i>Під повідомленням:</i> кнопки з’являються під повідомленням. При натисканні користувач переходить до іншого блоку. У більшості випадків попереднє повідомлення видаляється, щоб не засмічувати чат.<br>
    <i>Під полем введення:</i> кнопки знаходяться під полем введення тексту. Це застарілий варіант, через який у чаті накопичується багато повідомлень.<br>

    <b>Важливо:</b> один блок — один тип клавіатури.<br>

    <b>Контент</b>: блок може містити текст і зображення. Усе додане відображається у боті.<br>
    `,
  },
  {
    id: 'buttons',
    title: '2. Керування кнопками. Види кнопок.',
    html: `Для керування кнопками використовується вкладка "Кнопки".<br>
    <img class="instruction-image" src="/instruction/3.png"/><br>

    <b>Додати кнопку</b>: додає нову кнопку до блоку.<br>

    <b>Шаблонні кнопки</b>: швидкі варіанти кнопок. Деякі з них не можна редагувати (наприклад, "Пошук"), оскільки вони мають вбудовану логіку.<br>

    <b>Список кнопок</b>: перелік кнопок, які вже додані до блоку. Ліворуч є індикатор для зміни порядку. При натисканні відкривається редагування кнопки.<br>

    <img class="instruction-image" src="/instruction/4.png"/><br>

    <b>Напис</b>: текст кнопки, який бачить користувач.<br>

    <b>Тип кнопки</b>: визначає поведінку кнопки:<br>
    - Без дії: нічого не відбувається при натисканні.<br>
    - Посилання: відкриває сайт у браузері.<br>
    - Web App: відкриває додаток у Telegram.<br>
    - Callback: переводить користувача до іншого блоку (основна логіка бота).<br>
    - Програмні: спеціальні кнопки з фіксованою логікою (наприклад, "Пошук").<br>

    <i>Як працює callback-кнопка?</i><br>
    Користувач бачить "Головне меню":<br>
    <img class="instruction-image" src="/instruction/1.png"/><br>

    Натискає "Святкові торти":<br>
    <img class="instruction-image" src="/instruction/5.png"/><br>

    Отримує новий блок "Святкові торти".<br>

    Таким чином будується ланцюг:<br>
    Блок 1 → Блок 2 → Блок 3<br>

    <b>Розміщення кнопок</b>: визначає їх вигляд. Є два варіанти — у повну ширину екрана (1 ряд = 1 кнопка) або в ряд (кілька кнопок в одному рядку).<br>

    <i>Приклад кнопки у повну ширину:</i><br>
    <img class="instruction-image" src="/instruction/5.png"/><br>
    У цьому випадку кожна кнопка займає всю ширину екрана.<br><br>

    <i>Приклад кнопок в одному рядку:</i><br>
    <img class="instruction-image" src="/instruction/7.png"/><br>

    <i>Та під полем введення користувача:</i><br>
    <img class="instruction-image" src="/instruction/8.jpg"/><br>

    <b>Важливо пам'ятати</b><br>
    Перейти з кнопок під повідомленням до кнопок під текстовим полем — легко. Повернутися назад складніше: кнопки під полем введення залишаються, поки їх не замінить інший подібний блок.<br><br>

    <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
      <thead>
        <tr>
          <th align="left">Рекомендація</th>
          <th align="left">Кількість</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Кнопки під повідомленням (1 кнопка = 1 ряд)</td>
          <td>10</td>
        </tr>
        <tr>
          <td>Кнопки під повідомленням в одному ряду</td>
          <td>4</td>
        </tr>
        <tr>
          <td>Кнопки під повідомленням (загальна кількість)</td>
          <td>20</td>
        </tr>
        <tr>
          <td>Кнопки під текстовим полем (1 кнопка = 1 ряд)</td>
          <td>8</td>
        </tr>
        <tr>
          <td>Кнопки під текстовим полем в одному ряду</td>
          <td>3</td>
        </tr>
        <tr>
          <td>Кнопки під текстовим полем (загальна кількість)</td>
          <td>12</td>
        </tr>
      </tbody>
    </table>
    `,
  },
  {
    id: 'catalogs',
    title: '3. Керування каталогами та структурами.',
    html: `Для відображення продукції використовується наступна схема: Категорії -> Підкатегорії (якщо є) -> Каталог -> Продукція. <br>
    Простий приклад: Категорія "Торти" -> Підкатегорія "Milk-Cream" -> Каталог №1 -> Медовик. <br>
    <img class="instruction-image" src="/instruction/18.png"/>
    <img class="instruction-image" src="/instruction/19.png"/>
    <img class="instruction-image" src="/instruction/22.png"/><br>
    Редагування порядку категорій / підкатегорій реалізовано за допомогою поля "Порядок".<br> 
    <img class="instruction-image" src="/instruction/20.png"/><br>
    Для відображення продукту у боті необхідно закріпити його до певного каталогу.<br>
    Якщо у каталозі декілька підкаталогів, використовується управління <a href="/structure">структурою</a>.<br> 
    <img class="instruction-image" src="/instruction/21.png"/><br>
    Для управління порядком використовується індекатор на лівій частині блоків каталогу. Користувач переміщується між каталогами за допомогою кнопок "Далі" або "Назад".<br>
    `,
  },
  {
    id: 'products',
    title: '4. Керування продукцією.',
    html: `Для керування продукцією використовується <a href="/products">спеціальний розділ</a> адмінпанелі.<br>
    Продукція одразу відображається у боті, але необхідно обрати <b>каталог</b>.<br>
    Також потрібно обрати опцію "Відобразити продукт у боті".<br>
    Існують два типи цін: прості та варіаційні. Обидва типи можуть бути звичайні, підвищені або акційні.<br>
    <b>Звичайна ціна</b> — для всіх користувачів, окрім тих, кому показується підвищена.<br>
    <b>Підвищена ціна</b> — для користувачів із зазначеними містами у <a href="/settings">налаштуваннях</a>.<br>
    <b>Акційна ціна</b> — для акційних продуктів.<br>
    Звичайні ціни — лише числовий показник (100, 200, 500 або інший). Один продукт = одна ціна.<br> 
    <img class="instruction-image" src="/instruction/23.png"/>
    <img class="instruction-image" src="/instruction/24.png"/><br>
    Варіаційні ціни дозволяють вказати вагу та ціну. Максимальна кількість варіаційних цін — 5.<br>
    <img class="instruction-image" src="/instruction/25.png"/>
    <img class="instruction-image" src="/instruction/26.png"/><br>
    `,
  },
];

const Instructions: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [image, setImage] = React.useState<string | null>(null);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const src = (target as HTMLImageElement).src;
      setImage(src);
      setOpen(true);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={3}>
        Інструкція
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Box
            component="video"
            src="/vatsak_instr.mp4"
            controls
            sx={{ width: '100%', borderRadius: 2 }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardHeader title="Зміст" />
        <CardContent>
          <List>
            {sections.map((s) => (
              <ListItemButton key={s.id} onClick={() => scrollTo(s.id)}>
                <ListItemText primary={s.title} />
              </ListItemButton>
            ))}
          </List>
        </CardContent>
      </Card>

      <Stack spacing={3}>
        {sections.map((section) => (
          <Card
            key={section.id}
            id={section.id}
            sx={{
              borderRadius: 4,
              border: '2px solid #a855f7',
              background: '#faf5ff',
            }}
          >
            <CardHeader title={section.title} />
            <Divider />
            <CardContent>
              <Box
                onClick={handleClick}
                sx={{
                  cursor: 'pointer',
                  '& img': {
                    maxWidth: '100%',
                    borderRadius: 2,
                    margin: '12px 0',
                  },
                  '& .instruction-image': {
                    width: 250,
                    height: 250,
                    objectFit: 'cover',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: section.html }}
              />
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        {image && (
          <Box
            component="img"
            src={image}
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
            }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default Instructions;
